<template>
  <div class="view" v-if="canEdit || canDelete" v-cloak>
    <EditCreateTitleBox :label="title">
      <!-- optional action buttons here -->
    </EditCreateTitleBox>
    <div class="work-machine-add-edit-container">
      <!-- Left Box -->
      <StepsPanelWrapper
        ref="stepsWrapper"
        :steps="steps"
        :current-step.sync="currentStep"
        :step-props-fn="getStepProps"
        @step-input="onStepInput"
        @validation-error="handleValidationError"
      />
      <!-- Right Box -->
      <work-machine-right-panel v-model="data" @save="save" @goto-list="gotoList" :saving="saving" />
    </div>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import { canDelete, canEdit, permissions, permissionTypes } from "@/helpers/Permissions";
import * as actions from "@/helpers/generalActions";
import Forbidden from "@/pages/errors/Forbidden";
import { only_numbers, successMessage, handlePromiseException, errorMessage } from "@/helpers/common";
import WorkMachineFormStep from "./partials/WorkMachineFormStep.vue";
import WorkMachineExpirationStep from "./partials/WorkMachineExpirationStep.vue";
import WorkMachineRightPanel from "./partials/WorkMachineRightPanel.vue";
import EditCreateTitleBox from "@/components/Common/EditCreateTitleBox.vue";
import StepsPanelWrapper from "@/components/Common/StepsPanelWrapper.vue";

export default {
  name: "WorkMachineDetails",
  components: {
    Forbidden,
    WorkMachineFormStep,
    WorkMachineRightPanel,
    WorkMachineExpirationStep,
    EditCreateTitleBox,
    StepsPanelWrapper,
  },
  data() {
    return {
      data: {},
      autopropulsat: false,
      equipment: false,
      is_dataller: false,
      steps: [
        {
          number: 1,
          label: this.$t("common.settings"),
          component: WorkMachineFormStep,
          ref: "WorkMachineFormStep",
        },
        {
          number: 2,
          label: this.$t("works.work_machines.expiration_documents"),
          component: WorkMachineExpirationStep,
          ref: "WorkMachineExpirationStep",
        },
      ],
      currentStep: 1,
      saving: false,
    };
  },
  computed: {
    name() {
      return this.$t("works.work_machines.name");
    },
    config() {
      return {
        permission: permissions.WORK_MACHINES,
        permission_type: permissionTypes.access,
        get: this.get,
        create: this.create,
        update: this.update,
        delete: this.delete,
        list_url: "/works/work-machines",
        confirm_delete: true,
      };
    },
    actions() {
      return actions;
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this) && !!this.data.is_deletable;
    },
    title() {
      return (this.data.uuid ? this.$t("common.edit") : this.$t("common.new")) + " (" + this.name?.toString().toLowerCase() + ")";
    },
  },
  methods: {
    ...mapActions({
      getWorks: "works-manager/getWorks",
      get: "works-work-machines/getWorkMachine",
      create: "works-work-machines/createWorkMachine",
      update: "works-work-machines/updateWorkMachine",
      delete: "works-work-machines/deleteWorkMachine",
    }),
    onlyNumbers(event) {
      return only_numbers(event);
    },
    loadData() {
      return this.actions.getData(this, () => {
        this.data.parent_uuid = null;
      });
    },
    gotoList() {
      return actions.gotoList(this);
    },
    async save() {
      const errors = await this.validateSteps();
      if (errors.length) {
        errors.forEach((msg) => errorMessage(this, msg));
        return;
      }
      this.saving = true;
      this.data.autopropulsat = this.autopropulsat ? 1 : 0;
      this.data.equipment = this.equipment ? 1 : 0;
      this.data.is_dataller = this.is_dataller ? 1 : 0;
      if (Array.isArray(this.data.expiration_documents)) {
        this.data.expiration_documents = this.data.expiration_documents.filter((d) => d && d.expiration_date);
      }

      if (this.data.uuid) {
        this.update(this.data)
          .then((response) => {
            successMessage(this, response.message);
            this.$router.push({ name: "work-machine-list" });
          })
          .catch((e) => {
            handlePromiseException(this, e);
          })
          .finally(() => {
            this.saving = false;
          });
      } else {
        this.create(this.data)
          .then((response) => {
            successMessage(this, response.message);
            this.$router.push({ name: "work-machine-list" });
          })
          .catch((e) => {
            handlePromiseException(this, e);
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    async validateSteps() {
      const wrapper = this.$refs?.stepsWrapper;
      if (wrapper && typeof wrapper.validateSteps === "function") {
        const res = await Promise.resolve(wrapper.validateSteps());
        return Array.isArray(res) ? res : [];
      }
      return [];
    },
    onAutopropulsatChanged(val) {
      this.data.autopropulsat = val ? 1 : 0;
    },
    onEquipmentChanged(val) {
      this.data.equipment = val ? 1 : 0;
    },
    onIsDatallerChanged(val) {
      this.data.is_dataller = val ? 1 : 0;
    },
    getStepProps(step) {
      if (!step) return {};
      if (step.number === 1) {
        return { value: this.data };
      }
      if (step.number === 2) {
        return { value: this.data.expiration_documents || [] };
      }
      return {};
    },
    onStepInput(step, payload) {
      if (!step) return;
      if (step.number === 1) {
        this.data = { ...this.data, ...(payload || {}) };
        return;
      }
      if (step.number === 2) {
        this.$set(this.data, "expiration_documents", Array.isArray(payload) ? payload : []);
      }
    },
    handleValidationError(error) {
      // Handle validation error from StepsPanelWrapper
      errorMessage(this, error.message);
    },
  },
  mounted() {
    this.loadData();
    this.actions.handleParams(this);
  },
  watch: {
    data: {
      handler: function (newVal) {
        this.autopropulsat = Boolean(newVal.autopropulsat);
        this.equipment = Boolean(newVal.equipment);
        this.is_dataller = Boolean(newVal.is_dataller);
      },
      deep: true,
    },
    $route: {
      handler: function () {
        this.actions.handleParams(this);
      },
      deep: true,
    },
    permission: {
      handler: function (newVal) {
        this.permissionModule = newVal;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.view {
  height: calc(100% - 70px);
}

.work-machine-add-edit-container {
  display: flex;
  height: 100%;
  width:  100%;
}
</style>
